import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import {
  type InterviewBasicDtoPagedResponseDto,
  InterviewService,
  type InterviewTemplateBasicDtoPagedResponseDto,
  InterviewTemplateService,
  type Status,
} from '@/services/api';

type GetInterviewsParams = {
  size: number;
  page?: number;
  q?: string;
  status?: Array<Status>;
  startDateFrom?: Date;
  startDateTo?: Date;
  isTemplateInterview?: boolean;
};

export const useGetInterviewsQueries = (
  params: GetInterviewsParams,
  options?: Partial<
    UseQueryOptions<InterviewBasicDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<InterviewBasicDtoPagedResponseDto, AxiosError>({
    queryKey: ['getInterviews', params],
    queryFn: () =>
      InterviewService.getInterviews({
        ...params,
        startDateFrom: params.startDateFrom?.toISOString(),
        startDateTo: params.startDateTo?.toISOString(),
      }),
    // select: selectInterviewsData,
    ...options,
  });
};

export const useRemoveInterviewMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, { interviewsId: string }>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { interviewsId: string }>({
    mutationFn: (params) => InterviewService.deleteInterviewsById(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: ['getInterviews'],
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type GetInterviewTemplateParams = {
  size: number;
  page: number;
  q?: string;
};

export const useGetTemplateInterviewsQueries = (
  params: GetInterviewTemplateParams,
  options?: Partial<
    UseQueryOptions<InterviewTemplateBasicDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<InterviewTemplateBasicDtoPagedResponseDto, AxiosError>({
    queryKey: ['getInterviewTemplates', params],
    queryFn: () =>
      InterviewTemplateService.getInterviewTemplate({
        ...params,
      }),
    ...options,
  });
};
